/**
 * English message catalogue — the source of truth.
 *
 * Every other language is typed against this object, so a missing or misspelt
 * key is a compile error rather than a blank space in production. Keys are
 * grouped by surface and read as a path, which keeps them findable when a
 * string needs changing.
 *
 * Placeholders are `{name}` and are substituted at render time. Never build a
 * sentence by concatenating fragments: word order differs between languages and
 * the result is unlocalisable.
 */
export const en = {
  /* ------------------------------------------------------------- common */
  'common.back': 'Back',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.copy': 'Copy link',
  'common.copied': 'Copied',
  'common.copyShort': 'Copy',
  'common.loading': 'Loading…',
  'common.save': 'Save',
  'common.signOut': 'Log out',
  'common.settings': 'Settings',
  'common.profile': 'Profile',
  'common.branding': 'Chat branding',
  'common.search': 'Search',
  'common.menu': 'Menu',
  'common.retry': 'Try again',

  /* ------------------------------------------------------------- errors */
  'error.generic': 'Something went wrong.',
  'error.notFound': 'Not found',
  'error.notFoundBody': 'There is nothing at this address.',

  /* -------------------------------------------------------------- login */
  'login.title': 'Sign in',
  'login.subtitle': 'No passwords. We email you a one-time link.',
  'login.emailLabel': 'Email address',
  'login.submit': 'Email me a sign-in link',
  'login.sentTitle': 'Check your email',
  'login.sentBody':
    'If that address has an account, a sign-in link is on its way. It expires in {minutes} minutes and can be used once.',
  'login.sentNoticeTitle': 'Opening the link will not sign you in on its own',
  'login.sentNoticeBody':
    'You will be asked to press Continue. That is deliberate, so email scanners and link previews cannot consume your link before you use it.',
  'login.useDifferent': 'Use a different address',
  'login.emailNoticeTitle': 'Your email address is not end-to-end encrypted',
  'login.emailNoticeBody':
    'It is an account record, used to sign you in and to tell you when someone joins one of your links. Conversation content is a separate matter and is encrypted in your browser.',
  'login.failed': 'The sign-in link could not be sent. Try again shortly.',
  'login.backToStart': 'Back to the start',

  /* ------------------------------------------------------------ confirm */
  'confirm.checking': 'Checking this sign-in link…',
  'confirm.title': 'Finish signing in',
  'confirm.subtitle': 'One more step, on purpose.',
  'confirm.as': 'You are about to sign in as {email}.',
  'confirm.explain':
    'Nothing has happened yet. This link is still unused, and stays that way until you press Continue — which is why a scanner opening it cannot sign you in.',
  'confirm.continue': 'Continue',
  'confirm.unavailable': 'Sign-in link unavailable',
  'confirm.expired': 'This sign-in link has expired. Links last {minutes} minutes.',
  'confirm.used': 'This sign-in link has already been used. Each link works once.',
  'confirm.invalid': 'This sign-in link is not valid.',
  'confirm.requestNew': 'Request a new link',
  'confirm.failed': 'Signing in failed. Request a new link.',

  /* ---------------------------------------------------------- messenger */
  'messenger.newConversation': 'New link',
  'messenger.newConversationHint':
    'Creates a private link. Send it to one person; they enter a name and the conversation starts.',
  'messenger.creatingLink': 'Creating…',
  'messenger.someoneJoined': 'Someone joined one of your links.',
  'messenger.searchPlaceholder': 'Search',
  'messenger.clearSearch': 'Clear search',
  'messenger.filterAll': 'All',
  'messenger.filterUnread': 'Unread',
  'messenger.filterActive': 'Active',
  'messenger.filterEnded': 'Ended',
  'messenger.emptyTitle': 'No conversations yet',
  'messenger.emptyBody': 'Create a link and send it to one person to get started.',
  'messenger.noMatch': 'Nothing matches this search.',
  'messenger.loadingList': 'Loading conversations…',
  'messenger.welcomeTitle': 'Kairelo',
  'messenger.welcomeBody':
    'Send someone a link and talk for {hours} hours. Messages are encrypted in your browser, and nothing is kept afterwards.',
  'messenger.encrypted': 'End-to-end encrypted',
  'messenger.opening': 'Opening conversation…',
  'messenger.notOpenedYet':
    'Nobody has opened this invitation yet. It becomes a conversation when someone joins.',
  'messenger.ended': 'This conversation has ended. There is no transcript.',
  'messenger.suspendedTitle': 'Account suspended',
  'messenger.suspendedBody': 'Existing links have been revoked and new ones cannot be created.',

  /* ------------------------------------------------- conversation rows */
  'row.unusedInvitation': 'Unused invitation',
  'row.invitationTitle': 'Invitation',
  'row.invitationWaiting': 'Waiting to be opened',
  'row.invitationExpiresToday': 'Expires today',
  'row.invitationExpiresIn': 'Expires in {days} days',
  'row.visitor': 'Visitor',
  'row.conversation': 'Conversation',
  'row.notOpened': 'Invitation not opened yet',
  'row.ended': 'This conversation has ended',
  'row.deleted': 'Deleted',
  'row.noKey': 'No key in this browser',
  'row.joined': 'Joined',
  'row.joinedSayHello': 'Joined — say hello',
  'row.messageCount': '{count} messages',
  'row.messageCountOne': '1 message',
  'row.yesterday': 'Yesterday',

  /* --------------------------------------------------------------- chat */
  'chat.online': 'online',
  'chat.offline': 'offline',
  'chat.typing': 'typing…',
  'chat.connecting': 'connecting…',
  'chat.reconnecting': 'reconnecting…',
  'chat.conversationEnded': 'ended',
  'chat.back': 'Back to conversations',
  'chat.about': 'About this conversation',
  'chat.report': 'Report',
  'chat.encryptionBanner':
    'Messages are encrypted in your browser. This conversation ends by itself and leaves no transcript.',
  'chat.emptyWithKey': 'No messages yet. Anything you send is encrypted here first.',
  'chat.emptyNoKey':
    'This browser does not hold the key for this conversation, so its messages cannot be read here.',
  'chat.endedComposer': 'This conversation has ended.',
  'chat.endNow': 'End conversation now',
  'chat.endTitle': 'End this conversation now?',
  'chat.endBody':
    'Access ends immediately for both of you, and the encrypted copy the service holds enters deletion on the usual schedule.',
  'chat.endCaveat':
    'It cannot recall anything the other person already saw, saved or screenshotted. Nothing here can.',
  'chat.endConfirm': 'End now',
  'chat.ended': 'Conversation ended.',
  'chat.endFailed': 'The conversation could not be ended.',
  'chat.clearFromList': 'Clear from list',
  'chat.today': 'Today',
  'chat.yesterday': 'Yesterday',

  /* ----------------------------------------------------------- messages */
  'message.unreadable': 'Encrypted message that cannot be read in this browser',
  'message.tapToDecrypt': 'Tap to decrypt · {size}',
  'message.decrypting': 'Decrypting…',
  'message.couldNotOpen': 'Could not be opened',
  'message.decryptAndDownload': 'Decrypt and download',
  'message.save': 'Save {filename}',
  'message.testAdapter': 'test adapter',
  'message.sending': 'Sending',
  'message.sent': 'Sent',
  'message.failed': 'Not sent',
  'message.playVoice': 'Play voice message',
  'message.pauseVoice': 'Pause voice message',
  'message.playbackPosition': 'Playback position',

  /* ----------------------------------------------------------- composer */
  'composer.placeholder': 'Type a message',
  'composer.disabled': 'No key held in this browser',
  'composer.emoji': 'Emoji',
  'composer.attach': 'Attach',
  'composer.photosVideos': 'Photos & videos',
  'composer.document': 'Document',
  'composer.record': 'Record a voice message',
  'composer.stopAndSend': 'Stop recording and send',
  'composer.cancelRecording': 'Cancel recording',
  'composer.recording': 'Recording',
  'composer.send': 'Send',
  'composer.notConnected': 'Not connected. The message was not sent.',
  'composer.fileFailed': 'That file could not be sent.',
  'composer.voiceFailed': 'The voice message was not sent.',

  /* ------------------------------------------------------------- invite */
  'invite.title': 'Invitation link',
  'invite.subtitle': 'Shown once. The key it contains never reached the server.',
  'invite.reveal': 'Click to reveal',
  'invite.revealLabel': 'Reveal the invitation link',
  'invite.copied': 'Link copied. Send it to one person only.',
  'invite.failed': 'The link could not be created.',
  'invite.whatToKnow': 'What to know about this link',
  'invite.blockedTitle': 'New links are blocked in this browser',
  'invite.notCreated':
    '{detail} No link was created, because the key could not be kept in this browser and the conversation would be unreadable.',

  /* --------------------------------------------------------------- join */
  'join.opening': 'Opening invitation…',
  'join.defaultBio':
    '{name} uses Kairelo for short, private conversations. Messages are encrypted in your browser, and this one ends by itself with no transcript kept.',
  'join.invitedBy': 'You have been invited to a private conversation',
  'join.title': 'Join this conversation',
  'join.subtitle':
    'Choose a name to be known by. It is encrypted in your browser, so the service never sees it.',
  'join.nameLabel': 'Your display name',
  'join.namePlaceholder': 'What should they call you?',
  'join.nameHint':
    'Typing here does nothing on its own. The conversation starts only when you press Join.',
  'join.submit': 'Join conversation',
  'join.failed': 'Joining failed. Try again shortly.',
  'join.trustEncrypted': 'Encrypted in your browser',
  'join.trustEnds': 'Ends after {hours} hours',
  'join.trustNoAccount': 'No account needed',
  'join.knowBefore': 'What you should know before joining',
  'join.unusableTitle': 'This link cannot be used',
  'join.claimed':
    'Someone has already joined using this link. Each invitation admits exactly one visitor, and later attempts are refused even with the correct link.',
  'join.expired':
    'This invitation has expired. Unused invitations last 30 days, and a conversation lasts {hours} hours once it starts.',
  'join.invalid': 'This invitation link is not valid.',
  'join.askForNew': 'Ask the person who sent it for a new link.',
  'join.incompleteTitle': 'This link is incomplete',
  'join.incompleteBody':
    'The part after the # holds the key that decrypts this conversation. It never reaches the server, so it cannot be looked up or resent.',
  'join.incompleteHint':
    'The link was probably shortened, truncated by a messaging app, or copied without its end. Ask for the full link again.',

  /* ------------------------------------------------------------ visitor */
  'visitor.languageNote':
    'Kept in this browser only. It sets the interface and the language messages you receive are translated into.',
  'visitor.reconnecting': 'Reconnecting…',
  'visitor.noSessionTitle': 'This browser is not part of this conversation',
  'visitor.noSessionBody':
    'Joining creates a session credential stored in the browser that joined, and nowhere else. This browser does not have one.',
  'visitor.noSessionRecovery':
    'If you joined here and cleared your browsing data, the conversation cannot be recovered — there is no account to fall back on.',
  'visitor.endedTitle': 'This conversation has ended',
  'visitor.endedBody':
    'The {hours}-hour period is over. There is no transcript, and the encrypted copy the service held is queued for deletion.',
  'visitor.goToStart': 'Go to the start',

  /* ------------------------------------------------------------- report */
  'report.title': 'Report this conversation',
  'report.subtitle': 'Anything you write here is sent unencrypted and stored.',
  'report.warningTitle': 'The service cannot read your conversation',
  'report.warningBody':
    'It is encrypted, so a report cannot be checked against its content. What you write below is stored in plain text along with operational details such as timestamps and your IP address.',
  'report.reason': 'Reason',
  'report.reasonAbuse': 'Abuse',
  'report.reasonHarassment': 'Harassment',
  'report.reasonIllegal': 'Illegal content',
  'report.reasonSpam': 'Spam',
  'report.reasonOther': 'Other',
  'report.details': 'What happened',
  'report.detailsHint': 'Optional. Stored unencrypted.',
  'report.submit': 'Send report',
  'report.sentTitle': 'Report received',
  'report.sentBody': 'The report has been recorded with the conversation’s operational details.',
  'report.thanks': 'Thank you',
  'report.failed': 'The report could not be sent. Try again shortly.',

  /* ----------------------------------------------------------- settings */
  'settings.title': 'Settings',
  'settings.languageTitle': 'Language',
  'settings.languageSubtitle': 'Used for the interface and for translating messages you receive.',
  'settings.languageLabel': 'Your language',
  'settings.languageHint':
    'Incoming messages are translated into this language on your own device. Nothing is sent to a translation service.',
  'settings.languageSaved': 'Language saved.',
  'settings.languageFailed': 'The language could not be saved.',
  'settings.interfaceUnavailableTitle': 'On-device translation is unavailable in this browser',
  'settings.interfaceUnavailableBody':
    'Your language choice still applies to the interface. Messages will arrive in the language they were written in, because translating them would mean sending them to a service — which this product does not do.',
  'settings.translationAvailableTitle': 'On-device translation is available',
  'settings.translationAvailableBody':
    'Messages you receive are translated into your language in this browser, automatically. Nothing is sent to a translation service.',
  'settings.interfaceTitle': 'Interface',
  'settings.interfaceSubtitle':
    'How the chat looks. More styles are planned; only one is available today.',
  'settings.interfaceLabel': 'Interface style',
  'settings.accountTitle': 'Your account',
  'settings.backToConversations': 'Back to conversations',
  'settings.uiTranslated': 'Interface translated',
  'settings.uiEnglish': 'Interface in English',
  'settings.uiEnglishNote':
    'Messages are still translated into this language; the interface itself is not translated yet.',

  /* ------------------------------------------------------------ landing */
  'landing.heroLine1': 'One room.',
  'landing.heroLine2': 'One conversation.',
  'landing.heroLine3': 'Gone in 24h.',
  'landing.heroBody':
    'Share a link. The other person enters a name and joins. You talk for a day, and then it is over — no transcript, no archive, nothing to delete.',
  'landing.signIn': 'Sign in with email',
  'landing.noAccount': 'No account. No download.',
  'landing.ringLabel': 'and it’s over',

  'landing.howTitle': 'How it works',
  'landing.step1Title': 'Share a link',
  'landing.step1Body': 'You create a link and share it with one person.',
  'landing.step2Title': 'They enter a name',
  'landing.step2Body': 'No account, no download. A display name is enough.',
  'landing.step3Title': 'Chat for 24 hours',
  'landing.step3Body': 'After that, everything is gone.',

  'landing.getTitle': 'What you get',
  'landing.feature1Title': 'Share a private link',
  'landing.feature1Body':
    'Your browser makes the key and puts it in the link’s fragment — the part browsers never send to a server.',
  'landing.feature2Title': 'Enter a name and join',
  'landing.feature2Body':
    'No sign up, no account, no app. The other person types a display name and starts talking.',
  'landing.feature3Title': '24 hours and it is over',
  'landing.feature3Body':
    'Access ends, there is no transcript and no archive, and the encrypted copy is deleted.',
  'landing.feature4Title': 'End-to-end encrypted',
  'landing.feature4Body':
    'Messages, photos, videos, files and voice messages are encrypted in your browser before they are sent.',
  'landing.feature5Title': 'We cannot read it',
  'landing.feature5Body':
    'In ordinary operation the service relays and stores ciphertext and is never given the key.',

  'landing.carriesTitle': 'What a conversation carries',
  'landing.carriesMessages': 'Messages',
  'landing.carriesPhotos': 'Photos',
  'landing.carriesVideos': 'Videos',
  'landing.carriesFiles': 'Files',
  'landing.carriesVoice': 'Voice messages',

  'landing.notTitle': 'What this is not',
  'landing.notBody1':
    'It is not anonymous: the service still sees IP addresses, timestamps and browser information. It is not independently audited. It makes no claim of suitability for regulated professional use.',
  'landing.notBody2':
    'A conversation can only be read in the browser profile that created or joined it. Lose that profile and the conversation is unreadable — by anyone, including us.',
  'landing.languageLabel': 'Language',

  /* -------------------------------------------------------- disclosures */
  'disclosure.copyingTitle': 'Anything on screen can be copied',
  'disclosure.copyingBody':
    'Either person can screenshot, record or photograph this conversation. Encryption stops the service reading it; it cannot stop the person you are talking to keeping a copy.',
  'disclosure.bearerTitle': 'Treat the link as a secret',
  'disclosure.bearerBody':
    'Until someone joins, whoever holds this link can join first. Sending it through email or messaging, or leaving it in clipboard history, browser history or a screenshot, can expose it outside the service. Send it to one person and only that person.',
  'disclosure.deviceKeyTitle': 'Only this browser can read this conversation',
  'disclosure.deviceKeyBody':
    'The key is generated here and kept here. Signing in on another device will not let you read it, and following the email notification elsewhere will not either. Clearing this browser’s storage, using private browsing, or losing this device makes the conversation permanently unreadable. There is no recovery.',
  'disclosure.scopeTitle': 'What is and is not encrypted',
  'disclosure.scopeBody1':
    'Messages, photos, videos, files, voice messages and the visitor’s display name are encrypted in the browser. The owner’s profile, account email, and operational records such as timestamps and IP addresses are not, and are not described as end-to-end encrypted.',
  'disclosure.scopeBody2':
    'Because the encryption code is delivered as a web page, a compromised deployment of this service could serve modified code. That is a limitation of browser-delivered encryption and cannot be removed by design alone. The cryptography here has not been independently audited.',
  'disclosure.notAnonymousTitle': 'Registration-free is not anonymous',
  'disclosure.notAnonymousBody':
    'You do not need an account and your display name is encrypted. The service can still see operational data such as your IP address, timestamps and browser information, which it uses for rate limiting and abuse response.',
  'disclosure.temporaryTitle': 'This conversation ends by itself',
  'disclosure.temporaryBody':
    'It stays open for {hours} hours after it starts. After that neither side can reconnect, there is no transcript, and the service deletes the encrypted copy it holds. Backups age out separately on their own schedule.',
} as const;

export type MessageKey = keyof typeof en;
export type Catalogue = Record<MessageKey, string>;
